import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, NgZone, ElementRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { catchError, of, Subscription, timeout } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { FtcDocumentationService } from './ftc-documentation.service';

@Component({
    selector: 'gtw-ftc-documentation',
    templateUrl: './ftc-documentation.component.html',
    styleUrls: ['./ftc-documentation.component.scss']
    // Note: Using Default change detection and encapsulation for Angular Elements compatibility
    ,
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FtcDocumentationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Component properties
  minimise: boolean = false;
  loadingData: boolean = true;
  crudLoading: boolean = false;
  isSaveClicked: boolean = false;
  isEditMode: boolean = false;
  loading: boolean = true;

  // Dropdown visibility flags
  showParentMes: boolean = false;
  showDistinctMes: boolean = false;
  showHoEntities: boolean = false;
  showEachEntities: boolean = false;
  showFilingGrps: boolean = false;

  // Data properties
  filterTaxYear: string = '';
  scenario: string = '';
  jcd_key: string = '';
  logged_in_user: string = '';
  operationType: string = 'I';

  // Dropdown data
  levelCodeMastData: any[] = [];
  parentMEDropDown: any[] = [];
  distinctMEData: any[] = [];
  distinctHomeOfficeData: any[] = [];
  codeCombinationData: any[] = [];
  filingGroupData: any[] = [];

  // Selected values
  selectedLevel: any = null;
  selectedParentMe: any = null;
  selectedDistinctMe: any = null;
  selectedHoEntity: any = null;
  selectedLeEntity: any = null;
  selectedFilingGrp: any = null;

  // Lock status
  is_locked: any = null;

  // Grid data for edit mode
  gridDataMultipleSelect: any = null;

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '', 'base_url': '' };

  filterParams: any = {};

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  rowData: any = {};

  gridData: any = null;

  private initialized: boolean = false;
  private dataLoading: boolean = false;
  private levelDropdownLoaded: boolean = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private ftcDocumentationService: FtcDocumentationService,
    private ngZone: NgZone,
    private elementRef: ElementRef
  ) { }

  ngOnInit(): void {
    console.log('FTC Documentation ngOnInit called');
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.initialized = false;
    this.dataLoading = false;
    this.levelDropdownLoaded = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    this.globalService = gp;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = fp;
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : (row || {});
    this.gridData = gridPayloadFromActiveScreen(activeScreen);
  }

  /**
   * Try to initialize - will only proceed when all required data is available
   * Called from ngOnInit and from each input setter
   */
  tryInit(): void {
    // Already initialized? Skip.
    if (this.initialized) {
      console.log('FTC Documentation: Already initialized, skipping');
      return;
    }
    
    // Check if we have the required baseUrl
    const baseUrl = this.getBaseUrl();
    
    console.log('FTC Documentation tryInit():', {
      initialized: this.initialized,
      baseUrl: baseUrl,
      hasFilterParams: Object.keys(this.filterParams || {}).length > 0,
      hasGENERAL_CONFIG: Object.keys(this.GENERAL_CONFIG || {}).length > 0,
      hasBaseURLs: Object.keys(this.baseURLs || {}).length > 0,
      filterParams: this.filterParams,
      globalService: this.globalService
    });
    
    if (!baseUrl) {
      console.log('FTC Documentation: No baseUrl yet, waiting for inputs...');
      return;
    }
    
    // Ensure we have essential filter params or globalService values before initializing
    // This prevents making API calls with incomplete data
    const hasTaxYear = this.filterParams?.tax_year || this.globalService?.tax_year;
    const hasScenario = this.filterParams?.scenario || this.globalService?.scenario;
    const hasJcdKey = this.filterParams?.jcd_key || this.globalService?.jcd_key;
    
    if (!hasTaxYear || !hasScenario || !hasJcdKey) {
      console.log('FTC Documentation: Missing essential params (tax_year, scenario, jcd_key), waiting...');
      return;
    }
    
    // We have all required data - proceed with initialization
    // Set initialized flag BEFORE making API calls to prevent duplicate calls
    this.initialized = true;
    console.log('FTC Documentation: Initializing now with all required data!');

    // Set initial values - try filterParams first, then globalService
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
    this.filterTaxYear = this.filterParams?.tax_year || this.globalService?.tax_year || '';
    this.scenario = this.filterParams?.scenario || this.globalService?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.globalService?.jcd_key || '';

    console.log('FTC Documentation initialized with:', {
      logged_in_user: this.logged_in_user,
      filterTaxYear: this.filterTaxYear,
      scenario: this.scenario,
      jcd_key: this.jcd_key
    });

    // Load the Level dropdown
    this.loadLevelDropdown();
  }

  getBaseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || 
           this.GENERAL_CONFIG?.baseUrl || 
           this.baseURLs?.api || 
           this.baseURLs?.base_url || 
           '';
  }

  /**
   * Load only the Level dropdown on init - this is the primary data needed
   * Other dropdowns are loaded lazily when user selects a level
   */
  loadLevelDropdown(): void {
    // Prevent duplicate calls
    if (this.dataLoading) {
      console.log('FTC Documentation: Data already loading, skipping...');
      return;
    }
    
    // If already loaded successfully, don't reload
    if (this.levelDropdownLoaded && this.levelCodeMastData.length > 0) {
      console.log('FTC Documentation: Level dropdown already loaded, skipping...');
      return;
    }
    
    this.dataLoading = true;
    const baseUrl = this.getBaseUrl();
    
    // Ensure we have required params before making API call
    if (!this.filterTaxYear || !this.scenario || !this.jcd_key) {
      console.warn('FTC Documentation: Missing required params for API call:', {
        filterTaxYear: this.filterTaxYear,
        scenario: this.scenario,
        jcd_key: this.jcd_key
      });
      this.dataLoading = false;
      return;
    }
    
    console.log('FTC Documentation: Loading level dropdown from:', baseUrl, 'with params:', {
      tax_year: this.filterTaxYear,
      scenario: this.scenario,
      jcd_key: this.jcd_key
    });
    
    this.ngZone.run(() => {
      this.ftcDocumentationService.getDocLevelCodeMaster(baseUrl, this.globalService, this.filterParams)
        .pipe(
          timeout(10000), // 10 second timeout
          catchError((err: any) => {
            console.error('FTC Documentation: Error loading level dropdown:', err);
            this.dataLoading = false;
            return of({ jsonObject: [] });
          }), takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (response: any) => {
            console.log('FTC Documentation: Level dropdown response:', response);
            this.levelCodeMastData = response?.jsonObject || [];
            this.levelDropdownLoaded = true; // Mark as loaded
            console.log('FTC Documentation: levelCodeMastData:', this.levelCodeMastData);
            
            // Set saved values if in edit mode (will trigger loading of appropriate dropdown)
            this.setSavedValues();
            
            // Stop loading - form is now usable
            this.stopLoading();
          },
          error: (error: any) => {
            console.error('FTC Documentation: Error:', error);
            this.dataLoading = false;
            this.levelDropdownLoaded = false; // Reset on error so it can retry
            this.stopLoading();
            this.showError('Error loading data. Please try again.');
          }
        });
    });
  }

  /**
   * Load value dropdown data based on selected level
   * Called when user selects a level from dropdown
   */
  loadValueDropdownData(levelType: string): void {
    const baseUrl = this.getBaseUrl();
    this.loading = true;
    this.cdr.detectChanges();
    
    let dataCall: any;
    
    switch (levelType) {
      case 'PARENT_ME':
        if (this.parentMEDropDown.length > 0) {
          this.loading = false;
          this.cdr.detectChanges();
          return; // Already loaded
        }
        dataCall = this.ftcDocumentationService.getParentMeDropdown(
          baseUrl, this.logged_in_user, this.filterTaxYear, this.scenario, 
          this.globalService, this.filterParams
        );
        break;
      case 'ME':
        if (this.distinctMEData.length > 0) {
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
        dataCall = this.ftcDocumentationService.getDistinctMEData(
          baseUrl, this.filterTaxYear, this.globalService, this.filterParams
        );
        break;
      case 'HO_LE':
        if (this.distinctHomeOfficeData.length > 0) {
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
        dataCall = this.ftcDocumentationService.getDistinctHomeOfficeData(
          baseUrl, this.filterTaxYear, this.globalService, this.filterParams
        );
        break;
      case 'LE':
        if (this.codeCombinationData.length > 0) {
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
        dataCall = this.ftcDocumentationService.getDistinctLEIDsData(
          baseUrl, this.filterTaxYear, this.globalService, this.filterParams
        );
        break;
      case 'FG':
        if (this.filingGroupData.length > 0) {
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
        dataCall = this.ftcDocumentationService.getFilingGroupDropdown(
          baseUrl, this.globalService, this.filterParams
        );
        break;
      default:
        this.loading = false;
        this.cdr.detectChanges();
        return;
    }
    
    dataCall.pipe(
      timeout(10000),
      catchError((err: any) => {
        console.error(`FTC Documentation: Error loading ${levelType} data:`, err);
        return of({ jsonObject: [] });
      }), takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response: any) => {
        console.log(`FTC Documentation: ${levelType} data loaded:`, response);
        
        switch (levelType) {
          case 'PARENT_ME':
            this.parentMEDropDown = response?.jsonObject || [];
            break;
          case 'ME':
            this.distinctMEData = response?.jsonObject || [];
            break;
          case 'HO_LE':
            this.distinctHomeOfficeData = response?.jsonObject || [];
            break;
          case 'LE':
            this.codeCombinationData = response?.jsonObject || [];
            break;
          case 'FG':
            this.filingGroupData = response?.jsonObject || [];
            break;
        }
        
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error(`FTC Documentation: Error loading ${levelType}:`, error);
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }
  
  // Helper method to stop all loading states
  private stopLoading(): void {
    this.loadingData = false;
    this.loading = false;
    this.dataLoading = false;
    this.cdr.detectChanges();
  }

  setSavedValues(): void {
    if (this.gridData != null) {
      const tempGridData = _.clone(this.gridData);
      this.gridDataMultipleSelect = _.clone(tempGridData);

      if (this.gridDataMultipleSelect?.rowData) {
        // Edit mode
        this.operationType = 'U';
        this.isEditMode = true;

        // Find and set the selected level
        for (const level of this.levelCodeMastData) {
          if (level.CODE_KEY === this.gridDataMultipleSelect.rowData.LEVEL_KEY) {
            this.selectedLevel = level;
            
            // Set the appropriate value based on level type
            if (level.CODE_SHORT_DESC === 'PARENT_ME') {
              this.selectedParentMe = parseInt(this.gridDataMultipleSelect.rowData.VALUE_KEY);
            } else if (level.CODE_SHORT_DESC === 'ME') {
              this.selectedDistinctMe = parseInt(this.gridDataMultipleSelect.rowData.VALUE_KEY);
            } else if (level.CODE_SHORT_DESC === 'HO_LE') {
              this.selectedHoEntity = this.gridDataMultipleSelect.rowData.VALUE_KEY;
            } else if (level.CODE_SHORT_DESC === 'LE') {
              this.selectedLeEntity = parseInt(this.gridDataMultipleSelect.rowData.VALUE_KEY);
            } else if (level.CODE_SHORT_DESC === 'FG') {
              this.selectedFilingGrp = parseInt(this.gridDataMultipleSelect.rowData.VALUE_KEY);
            }
            break;
          }
        }
        
        this.onSelectOption();
      } else {
        // Add mode
        this.operationType = 'I';
        this.isEditMode = false;
        this.clearSelections();
      }
    }
  }

  clearSelections(): void {
    this.selectedLevel = null;
    this.selectedParentMe = null;
    this.selectedDistinctMe = null;
    this.selectedFilingGrp = null;
    this.selectedHoEntity = null;
    this.selectedLeEntity = null;
    this.loading = false;
  }

  onSelectOption(): void {
    // Reset all visibility flags
    this.showParentMes = false;
    this.showDistinctMes = false;
    this.showHoEntities = false;
    this.showEachEntities = false;
    this.showFilingGrps = false;

    if (this.selectedLevel) {
      const shortDesc = this.selectedLevel.CODE_SHORT_DESC;
      
      switch (shortDesc) {
        case 'PARENT_ME':
          this.showParentMes = true;
          break;
        case 'ME':
          this.showDistinctMes = true;
          break;
        case 'HO_LE':
          this.showHoEntities = true;
          break;
        case 'LE':
          this.showEachEntities = true;
          break;
        case 'FG':
          this.showFilingGrps = true;
          break;
      }
      
      // Lazy load the value dropdown data for the selected level
      this.loadValueDropdownData(shortDesc);
    } else {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onParentMeChange(event: any): void {
    this.selectedParentMe = event;
    this.cdr.detectChanges();
  }

  onDistinctMeChange(event: any): void {
    this.selectedDistinctMe = event;
    this.cdr.detectChanges();
  }

  onHoEntityChange(event: any): void {
    this.selectedHoEntity = event;
    this.cdr.detectChanges();
  }

  onLeEntityChange(event: any): void {
    this.selectedLeEntity = event;
    this.cdr.detectChanges();
  }

  save(): void {
    // Check lock status
    if (this.is_locked && this.is_locked.length > 0 && this.is_locked[0].LOCK_STATUS === 'Y') {
      this.cancel();
      this.showError('You cannot perform this action as Tax return is already filed.');
      return;
    }

    // Validate level selection
    if (!this.selectedLevel) {
      this.showError('Please select all the required fields.');
      return;
    }

    // Validate value selection based on level type
    const shortDesc = this.selectedLevel.CODE_SHORT_DESC;
    
    if (shortDesc === 'PARENT_ME' && !this.selectedParentMe) {
      this.showError('Please select a valid Parent ME');
      return;
    } else if (shortDesc === 'ME' && !this.selectedDistinctMe) {
      this.showError('Please select a valid ME.');
      return;
    } else if (shortDesc === 'HO_LE' && !this.selectedHoEntity) {
      this.showError('Please select a valid Home Office LEID.');
      return;
    } else if (shortDesc === 'LE' && !this.selectedLeEntity) {
      this.showError('Please select a valid LEID.');
      return;
    } else if (shortDesc === 'FG' && !this.selectedFilingGrp) {
      this.showError('Please select a valid Filing Group.');
      return;
    }

    // Disable save button
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build data to save
    const returnObj: any = {
      level_key: this.selectedLevel.CODE_KEY,
      operation_type: this.operationType
    };

    // Set value_key based on level type
    switch (shortDesc) {
      case 'PARENT_ME':
        returnObj.value_key = this.selectedParentMe;
        break;
      case 'ME':
        returnObj.value_key = this.selectedDistinctMe;
        break;
      case 'HO_LE':
        returnObj.value_key = this.selectedHoEntity;
        break;
      case 'LE':
        returnObj.value_key = this.selectedLeEntity;
        break;
      case 'FG':
        returnObj.value_key = this.selectedFilingGrp;
        break;
    }

    // Add update-specific fields
    if (this.operationType === 'U' && this.gridDataMultipleSelect?.rowData) {
      returnObj.document_key = this.gridDataMultipleSelect.rowData.DOCUMENT_KEY;
      returnObj.tax_year = this.gridDataMultipleSelect.rowData.TAX_YEAR;
    }

    const dataToBeSaved = [returnObj];

    // Build settings object - must include tax_year, scenario, jcd_key for backend
    // Match AngularJS implementation: get values from filterParams or globalService
    // AngularJS uses: vm.filterTaxYear, vm.scenario, GlobalService.globalParams.jcd_key
    const taxYearValue = this.filterTaxYear || this.filterParams?.tax_year || this.globalService?.tax_year;
    const scenarioValue = this.scenario || this.filterParams?.scenario || this.globalService?.scenario;
    const jcdKeyValue = this.jcd_key || this.filterParams?.jcd_key || this.globalService?.jcd_key;
    const screenKeyValue = this.globalService?.screen_key || '';
    
    // Convert to numbers - backend expects numeric values
    // Handle string, number, or null/undefined values
    const taxYearNum = taxYearValue ? Number(taxYearValue) : null;
    const scenarioNum = scenarioValue ? Number(scenarioValue) : null;
    const jcdKeyNum = jcdKeyValue ? Number(jcdKeyValue) : null;
    
    // Validate required numeric fields
    if (!taxYearNum || isNaN(taxYearNum)) {
      console.error('FTC Documentation Save: Invalid tax_year:', taxYearValue, 'from:', {
        filterTaxYear: this.filterTaxYear,
        filterParams: this.filterParams,
        globalService: this.globalService
      });
      this.showError('Tax Year is required and must be a valid number.');
      this.crudLoading = false;
      this.isSaveClicked = false;
      return;
    }
    if (!scenarioNum || isNaN(scenarioNum)) {
      console.error('FTC Documentation Save: Invalid scenario:', scenarioValue, 'from:', {
        scenario: this.scenario,
        filterParams: this.filterParams,
        globalService: this.globalService
      });
      this.showError('Scenario is required and must be a valid number.');
      this.crudLoading = false;
      this.isSaveClicked = false;
      return;
    }
    if (!jcdKeyNum || isNaN(jcdKeyNum)) {
      console.error('FTC Documentation Save: Invalid jcd_key:', jcdKeyValue, 'from:', {
        jcd_key: this.jcd_key,
        filterParams: this.filterParams,
        globalService: this.globalService
      });
      this.showError('JCD Key is required and must be a valid number.');
      this.crudLoading = false;
      this.isSaveClicked = false;
      return;
    }
    
    // Build settings object exactly like AngularJS
    const returnClobSettingsObj: any = {
      sso_id: this.logged_in_user || '',
      tax_year: taxYearNum,
      scenario: scenarioNum,
      screen_key: screenKeyValue,
      jcd_key: jcdKeyNum,
      object_id: this.gridDataMultipleSelect?.rowData?.object_id || ''
    };

    console.log('FTC Documentation - Save settings (matching AngularJS):', returnClobSettingsObj);
    console.log('FTC Documentation - Source values:', {
      filterTaxYear: this.filterTaxYear,
      scenario: this.scenario,
      jcd_key: this.jcd_key,
      filterParams: this.filterParams,
      globalService: this.globalService,
      converted: {
        tax_year: taxYearNum,
        scenario: scenarioNum,
        jcd_key: jcdKeyNum
      }
    });

    const baseUrl = this.getBaseUrl();

    this.ftcDocumentationService.saveData(baseUrl, returnClobSettingsObj, dataToBeSaved, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.crudLoading = false;
          this.isSaveClicked = false;
          
          if (result.errorMessage && result.errorMessage !== 'null') {
            this.showError(result.errorMessage);
          } else {
            this.showSuccess('Documentation related Level and Value has been successfully saved/updated');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error saving data:', error);
          this.crudLoading = false;
          this.isSaveClicked = false;
          this.showError('Error saving data. Please try again.');
          this.cdr.detectChanges();
        }
      );
  }

  reset(): void {
    if (this.operationType === 'I') {
      this.clearSelections();
      this.showParentMes = false;
      this.showDistinctMes = false;
      this.showHoEntities = false;
      this.showEachEntities = false;
      this.showFilingGrps = false;
    } else {
      this.setSavedValues();
    }
    this.cdr.detectChanges();
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  showError(message: string): void {
    this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--red', true, 4);
  }

  showSuccess(message: string): void {
    this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--green', true, 4);
  }

  // Track by function for ngFor
  trackByCodeKey(index: number, item: any): any {
    return item.CODE_KEY || index;
  }

  trackByKey(index: number, item: any): any {
    return item.KEY || index;
  }

  trackByValue(index: number, item: any): any {
    return item.VALUE || index;
  }
}
